import { FC, useEffect, useState } from 'react';
import { Pencil, Trash } from 'phosphor-react';
import { Addon, ShopContent } from '../../../types/cms_shared_types';
import { Conditional } from '../../Common/Conditional';
import { AddonDetailsModal } from '../Addons/AddonDetailsModal';
import { AddonListActions } from '../../../types/webshop';
import { ShoppingItemDesktop } from './ShoppingItemDesktop';
import { ShoppingItemMobile } from './ShoppingItemMobile';

export const caretTransitionVariants = {
  closed: { transformOrigin: 'center', rotate: 0, transition: { duration: 0.2 } },
  open: { transformOrigin: 'center', rotate: 180, transition: { duration: 0.2 } },
};

interface ShoppingAddonProps {
  addon: Addon;
  selectedAddons: Addon[];
  selectedAddonsActions: AddonListActions;
  withEdit: boolean;
  cmsShopContent: ShopContent;
}

export const ShoppingAddon: FC<ShoppingAddonProps> = ({
  addon,
  selectedAddons,
  selectedAddonsActions,
  withEdit,
  cmsShopContent,
}) => {
  const [isSelected, setIsSelected] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const addAddon = () => {
    selectedAddonsActions.add(addon as Addon);
  };

  const switchToAddon = () => {
    const selectedProviderAddons = selectedAddons.filter(
      (selectedAddon) => selectedAddon.addonProvider.uuid === addon.addonProvider.uuid,
    );

    selectedProviderAddons.forEach((selectedProviderAddon) => {
      const index = selectedAddons.findIndex((selectedAddon) => selectedAddon.code === selectedProviderAddon.code);
      selectedAddonsActions.remove(index);
    });

    addAddon();
  };

  const removeAddon = () => {
    const index = selectedAddons.findIndex((p) => p.code === addon.code);
    selectedAddonsActions.remove(index);
  };

  const Icon = () => {
    return (
      <button type="button" className="pl-2 ml-auto">
        <Conditional
          condition={withEdit}
          trueRender={<Pencil onClick={() => setShowModal(true)} size={32} className="text-sami-green" />}
          falseRender={
            <Trash
              onClick={() => {
                const prodIndex = selectedAddons.findIndex((p) => p.code === addon.code);
                selectedAddonsActions.remove(prodIndex);
              }}
              size={32}
              className="text-sami-red"
            />
          }
        />
      </button>
    );
  };

  useEffect(() => {
    setIsSelected(selectedAddons.filter((p) => p.code === addon.code).length === 1);
  }, [addon.code, selectedAddons]);

  return (
    <div>
      <div className="hidden w-full sm:block">
        <ShoppingItemDesktop
          title={addon.addonProvider.name}
          icon={Icon()}
          details={<div className="mt-2 text-sm text-text-dark">{addon.name}</div>}
          pricing={
            <>
              {addon.monthlyPriceExclVat && (
                <div className="text-xs font-bold truncate text-text-gray sm:text-base">
                  {Math.ceil(addon.monthlyPriceExclVat)} {cmsShopContent.priceWithMonthFormat}
                </div>
              )}
            </>
          }
          imageUrl={addon.addonProvider.imageUrl}
        />
      </div>

      <div className="block w-full sm:hidden">
        <ShoppingItemMobile
          title={addon.addonProvider.name}
          icon={Icon()}
          details={<div className="mt-2 text-sm text-text-dark">{addon.name}</div>}
          pricing={
            <>
              {addon.monthlyPriceExclVat && (
                <div className="pt-2 text-xs font-bold truncate text-text-gray sm:text-base">
                  {Math.ceil(addon.monthlyPriceExclVat)} {cmsShopContent.priceWithMonthFormat}
                </div>
              )}
            </>
          }
        />
      </div>
      <AddonDetailsModal
        addon={addon}
        isSelected={isSelected}
        showModal={showModal}
        onAdd={() => {
          addAddon();
          setShowModal(false);
        }}
        onSwitchTo={() => {
          switchToAddon();
          setShowModal(false);
        }}
        onConfirm={() => {
          setShowModal(false);
        }}
        onRemove={() => {
          removeAddon();
          setShowModal(false);
        }}
        cmsShopContent={cmsShopContent}
        setShowModal={setShowModal}
      />
    </div>
  );
};
